import { useDispatch, useSelector } from 'react-redux'
import {
	registerUsingActivationCode,
	updateProfile,
} from '../../../features/authSlice'
import { toastHelper } from '../../../util/toastHelper'
import InputCustom from '../../atoms/input/InputCustom'
import ButtonCustom from '../../atoms/button/ButtonCustom'
import styles from './user.module.scss'
import { Avatar } from '@mui/material'
import CheckBoxCustom from '../../atoms/checkbox/CheckBox'

// Component for collecting user's personal info during onboarding
export const YourInfo = ({ userDetails, setUserDetails, role, setRole }) => {
	const dispatch = useDispatch()

	const email = useSelector((state) => state.auth.user.email)

	// Handle advancing to the next step
	const handleSubmit = async (e) => {
		e.preventDefault()

		const { firstName, lastName } = userDetails
		// Validate required fields
		if (!firstName.trim() || !lastName.trim()) {
			return toastHelper(
				'Please fill in first name and last name',
				'error'
			)
		}

		if (userDetails.hasActivationCode) {
            if (!userDetails.activationCode?.trim()) {
                return toastHelper('Please enter a valid Wedding Join Code or uncheck the box if you don\'t have one', 'error')
            }
            
            try {
                // If Wedding Join Code exists, validate it
                const res = await dispatch(
                    registerUsingActivationCode({
                        verificationCode: userDetails.activationCode.trim(),
                        email,
                        role
                    })
                ).unwrap()

                if (!res || !res.success) {
                    return toastHelper('Incorrect Wedding Join Code. Please enter the correct code or leave it blank if you don\'t have one', 'error')
                }
            } catch (err) {
                console.error('Activation code validation failed:', err)
                return toastHelper('Failed to validate Wedding Join Code. Please try again.', 'error')
            }
        } else if (userDetails.activationCode?.trim()) {
            // If they entered a code but didn't check the box
            return toastHelper('Please check the "Wedding Join Code?" box if you have a code', 'error')
        }

        // If we get here, either:
        // 1. No activation code was provided (and none is required), or
        // 2. A valid activation code was provided and verified
        const {
            preferredName,
            phone,
            containsImage,
            imageMediaMap,
        } = userDetails
        dispatch(
            updateProfile({
                firstName,
                lastName,
                preferredName,
                phone,
                role: 'user',
                ...(containsImage ? { containsImage } : {}),
                ...(imageMediaMap ? { imageMediaMap } : {}),
            })
        )
    }

    return (
		<>
			{/* <h1>Your Info</h1> */}
			<div className={styles.uploadContainer}>
				<label
					htmlFor="profileUpload"
					className={styles.uploadCircle}
					style={{ margin: '0 auto' }}
				>
					{userDetails.profilePic ? (
						<Avatar
							src={userDetails.profilePic}
							alt="Profile"
							sx={{
                                width: '130px',
                                height: '130px'
                            }}
						/>
					) : (
						<span>
							Upload
							<br />A Photo
						</span>
					)}
					<div className={styles.cameraIcon}>📷</div>
				</label>
				<input
					id="profileUpload"
					type="file"
					accept=".jpg, .png"
					style={{ display: 'none' }}
					onChange={(e) => {
                        const file = e.target.files[0]
						if (!file) return

                        const maxSizeMB = 4 //Set the img size
                        const maxSizeInBytes = maxSizeMB * 1024 * 1024

                        if (file?.size > maxSizeInBytes) {
                            return toastHelper(
                                'Image size too large, please choose images less than 4mb',
                                'error'
                            )
                        }
                        const fileURL = URL.createObjectURL(file)
                        setUserDetails({
                            ...userDetails,
                            containsImage: 'profilePic',
                            profilePic: fileURL,
                            imageMediaMap: { [fileURL]: file }
                        })
                        
					}}
				/>
			</div>
			<form className={styles.form} onSubmit={handleSubmit}>
				<div className={styles.formGroup}>
					<InputCustom
						data-cy="firstName"
						placeholder="First Name"
						name="firstName"
						type="text"
						value={userDetails.firstName}
						setDataFunction={setUserDetails}
						data={userDetails}
						required={true}
					/>
				</div>
				<div className={styles.formGroup}>
					<InputCustom
						data-cy="lastName"
						placeholder="Last Name"
						name="lastName"
						type="text"
						value={userDetails.lastName}
						setDataFunction={setUserDetails}
						data={userDetails}
						required={true}
					/>
				</div>
				<div className={styles.formGroup}>
					<InputCustom
						data-cy="preferredName"
						placeholder="Preferred Name"
						name="preferredName"
						type="text"
						value={userDetails.preferredName}
						setDataFunction={setUserDetails}
						data={userDetails}
					/>
				</div>
				<div className={styles.formGroup}>
					<InputCustom
						data-cy="phoneNumber"
						placeholder="Phone Number"
						name="phone"
						type="text"
						value={userDetails.phone}
						setDataFunction={setUserDetails}
						data={userDetails}
						required={true}
					/>
				</div>
				<div className={styles.formGroup}>
                    <CheckBoxCustom
                        checked={userDetails.hasActivationCode}
                        onChange={() => {
                            setUserDetails({
                                ...userDetails,
                                hasActivationCode: !userDetails.hasActivationCode,
								activationCode: !userDetails.hasActivationCode ? userDetails.activationCode : ''
                            })
                        }}
                        label='Wedding Join Code?'
                        lineColor='#7ca6bd'
                        labelColor='#7ca6bd'
                    />
                    {
                        userDetails.hasActivationCode &&
                        <InputCustom
                            data-cy="activationCode"
                            placeholder="Wedding Join Code (If you got invited to a wedding)"
                            name="activationCode"
                            type="text"
                            value={userDetails.activationCode}
                            setDataFunction={setUserDetails}
                            data={userDetails}
                        />
                    }
				</div>
				<div className={`${styles.bottomButtons} ${styles.skipBtn}`}>
					<button
						data-cy="backButton"
						onClick={() => setRole('')}
						className="backButton"
					>
						Back
					</button>
					<ButtonCustom className={styles.button} type="submit">
						Submit
					</ButtonCustom>
				</div>
			</form>
		</>
	)
}
